/**
 * Converts Training Storyboard data to VR interaction format
 * This transforms chapters and moments into VR scene interactions
 * ASSET-CENTRIC: Everything revolves around assets and their tempId (matching publish temp_file_name)
 */

/**
 * Get asset tempId that matches what's used during publish
 * Priority: tempId > temp_file_name > id (from originalAsset) > id
 */
function getAssetTempId(asset) {
  // For fetched assets: tempId is already set
  if (asset.tempId) {
    return asset.tempId;
  }

  // For GridPage assets: check originalAsset for temp_file_name
  if (asset.originalAsset?.temp_file_name) {
    return asset.originalAsset.temp_file_name;
  }

  // For assets with reduxObject: check temp_file_name
  if (asset.reduxObject?.temp_file_name) {
    return asset.reduxObject.temp_file_name;
  }

  // Fallback to id (for GridPage assets that are already published)
  return asset.id;
}

/**
 * Convert training chapters to VR interaction format (ASSET-CENTRIC)
 * @param {Array} chapters - Training chapters array
 * @param {Array} requiredAssets - Required assets array
 * @returns {Object} VR interaction data structure with asset-centric schema
 */
export function convertTrainingToVR(chapters, requiredAssets = []) {
  // ASSET-CENTRIC STRUCTURE: Assets are first-class citizens
  const vrInteractions = {
    assets: [],
    assetMappings: {},
    chapters: [],
  };

  // Build asset-centric structure first
  requiredAssets.forEach((asset) => {
    const tempId = getAssetTempId(asset);

    // Create asset entry with tempId as primary identifier
    const assetEntry = {
      // PRIMARY ID: tempId matching publish temp_file_name
      id: tempId,
      tempId: tempId, // Explicit tempId field for Unity matching
      name: asset.name,
      type: asset.type || 'file',
      assetType: asset.assetType || asset.type || 'mesh',
      url: asset.url || '',
      description: asset.description || '',
      usage: asset.usage || '',
      mandatory: asset.mandatory || false,

      // Asset interaction moments (will be populated below)
      interactions: [],

      // Asset flow logic
      flow: {
        prerequisiteAssets: [], // Assets that must be interacted with first
        nextAssetAfterCompletion: null, // Next asset in sequence
        unlocksAssets: [], // Assets this unlocks
        blocksAssets: [], // Assets this blocks until done
      },

      // Asset state tracking
      state: {
        available: asset.status === 'available',
        interacted: false,
        interactionCount: 0,
      },
    };

    vrInteractions.assets.push(assetEntry);
    vrInteractions.assetMappings[tempId] = assetEntry;
  });

  // Convert chapters and map moments to assets
  chapters.forEach((chapter) => {
    const vrChapter = {
      id: chapter.id,
      number: chapter.number,
      title: chapter.title,
      moments: [],
      // Asset group for this chapter
      assetIds: [],
    };

    // Convert each moment
    chapter.moments?.forEach((moment) => {
      const technical = moment.content?.technical || {};
      const nonTechnical = moment.content?.nonTechnical || {};

      // Use technical mode for VR implementation
      const vrMoment = {
        id: moment.id,
        chapterId: chapter.id,
        name:
          technical.general?.name ||
          nonTechnical.general?.name ||
          'Untitled Moment',
        description:
          technical.general?.description ||
          nonTechnical.general?.description ||
          '',

        // EXPLICIT ASSET REFERENCE - using tempId
        assetId: null, // Will be set below
        assetName: null,
        assetTempId: null, // Explicit tempId for Unity matching

        // Interaction configuration
        interaction: {
          touchObjectQuery:
            technical.interaction?.touchObjectQuery ||
            nonTechnical.interaction?.touchObjectQuery ||
            '',
          interactionType:
            technical.interaction?.interactionType ||
            nonTechnical.interaction?.interactionType ||
            'poke',
          duration:
            technical.interaction?.duration ||
            nonTechnical.interaction?.duration ||
            0,
          required:
            technical.interaction?.required ??
            nonTechnical.interaction?.required ??
            true,
          // Place Point specific fields
          grabbableName:
            technical.interaction?.grabbableName ||
            nonTechnical.interaction?.grabbableName ||
            '',
          disableGrabOnPlace:
            technical.interaction?.disableGrabOnPlace ??
            nonTechnical.interaction?.disableGrabOnPlace ??
            false,
          disablePlacePointOnPlace:
            technical.interaction?.disablePlacePointOnPlace ??
            nonTechnical.interaction?.disablePlacePointOnPlace ??
            false,
          disableRigidbodyOnPlace:
            technical.interaction?.disableRigidbodyOnPlace ??
            nonTechnical.interaction?.disableRigidbodyOnPlace ??
            false,
        },

        // Before touch state
        beforeTouch: {
          voiceOver:
            technical.beforeTouch?.voiceOver ||
            nonTechnical.beforeTouch?.voiceOver ||
            '',
          displayText:
            technical.beforeTouch?.displayText ||
            nonTechnical.beforeTouch?.displayText ||
            '',
          objects: {
            query:
              technical.beforeTouch?.objects?.query ||
              nonTechnical.beforeTouch?.objects?.query ||
              '',
            option:
              technical.beforeTouch?.objects?.option ||
              nonTechnical.beforeTouch?.objects?.option ||
              'Highlight',
            spawn:
              technical.beforeTouch?.objects?.spawn ||
              nonTechnical.beforeTouch?.objects?.spawn ||
              '',
          },
          image:
            technical.beforeTouch?.image ||
            nonTechnical.beforeTouch?.image ||
            '',
          video:
            technical.beforeTouch?.video ||
            nonTechnical.beforeTouch?.video ||
            '',
          metaLayer:
            technical.beforeTouch?.metaLayer ||
            nonTechnical.beforeTouch?.metaLayer ||
            '',
          holoBooks:
            technical.beforeTouch?.holoBooks ||
            nonTechnical.beforeTouch?.holoBooks ||
            '',
          highlights:
            technical.beforeTouch?.highlights ||
            nonTechnical.beforeTouch?.highlights ||
            '',
          player:
            technical.beforeTouch?.player ||
            nonTechnical.beforeTouch?.player ||
            '',
          baseItem:
            technical.beforeTouch?.baseItem ||
            nonTechnical.beforeTouch?.baseItem ||
            '',
          objAnimation:
            technical.beforeTouch?.objAnimation ||
            nonTechnical.beforeTouch?.objAnimation ||
            '',
          uiUpdate:
            technical.beforeTouch?.uiUpdate ||
            nonTechnical.beforeTouch?.uiUpdate ||
            '',
          timerDelay:
            technical.beforeTouch?.timerDelay ||
            nonTechnical.beforeTouch?.timerDelay ||
            0,
          animation:
            technical.beforeTouch?.animation ||
            nonTechnical.beforeTouch?.animation ||
            '',
          stateTable:
            technical.beforeTouch?.stateTable ||
            nonTechnical.beforeTouch?.stateTable ||
            '',
          checklistUI:
            technical.beforeTouch?.checklistUI ||
            nonTechnical.beforeTouch?.checklistUI ||
            [],
        },

        // After touch state
        afterTouch: {
          voiceOver:
            technical.afterTouch?.voiceOver ||
            nonTechnical.afterTouch?.voiceOver ||
            '',
          displayText:
            technical.afterTouch?.displayText ||
            nonTechnical.afterTouch?.displayText ||
            '',
          visualEffect:
            technical.afterTouch?.visualEffect ||
            nonTechnical.afterTouch?.visualEffect ||
            '',
          sfx: technical.afterTouch?.sfx || nonTechnical.afterTouch?.sfx || '',
          metaLayer:
            technical.afterTouch?.metaLayer ||
            nonTechnical.afterTouch?.metaLayer ||
            '',
          objects: {
            query:
              technical.afterTouch?.objects?.query ||
              nonTechnical.afterTouch?.objects?.query ||
              '',
            option:
              technical.afterTouch?.objects?.option ||
              nonTechnical.afterTouch?.objects?.option ||
              '',
            spawn:
              technical.afterTouch?.objects?.spawn ||
              nonTechnical.afterTouch?.objects?.spawn ||
              '',
          },
          image:
            technical.afterTouch?.image || nonTechnical.afterTouch?.image || '',
          video:
            technical.afterTouch?.video || nonTechnical.afterTouch?.video || '',
          holoBooks:
            technical.afterTouch?.holoBooks ||
            nonTechnical.afterTouch?.holoBooks ||
            '',
          highlights:
            technical.afterTouch?.highlights ||
            nonTechnical.afterTouch?.highlights ||
            '',
          player:
            technical.afterTouch?.player ||
            nonTechnical.afterTouch?.player ||
            '',
          baseItem:
            technical.afterTouch?.baseItem ||
            nonTechnical.afterTouch?.baseItem ||
            '',
          objAnimation:
            technical.afterTouch?.objAnimation ||
            nonTechnical.afterTouch?.objAnimation ||
            '',
          uiUpdate:
            technical.afterTouch?.uiUpdate ||
            nonTechnical.afterTouch?.uiUpdate ||
            '',
          timerDelay:
            technical.afterTouch?.timerDelay ||
            nonTechnical.afterTouch?.timerDelay ||
            0,
          animation:
            technical.afterTouch?.animation ||
            nonTechnical.afterTouch?.animation ||
            '',
          stateTable:
            technical.afterTouch?.stateTable ||
            nonTechnical.afterTouch?.stateTable ||
            '',
          checklistUI:
            technical.afterTouch?.checklistUI ||
            nonTechnical.afterTouch?.checklistUI ||
            [],
        },

        // Voice warnings
        voiceWarnings: (
          technical.voiceWarnings ||
          nonTechnical.voiceWarnings ||
          []
        ).map((warning) => ({
          id: warning.id || Math.random().toString(36),
          message: warning.message || warning.text || '',
          trigger: warning.touchObjectQuery || warning.trigger || '',
        })),

        // Notes
        notes: technical.notes || nonTechnical.notes || '',
      };

      // Map asset to moment using touchObjectQuery matching (robust fuzzy matching)
      const touchQuery = vrMoment.interaction.touchObjectQuery;

      // Helper function for robust asset name matching
      const normalizeForMatching = (str) => {
        if (!str) return '';
        return str
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
      };

      const matchesAsset = (assetName, query) => {
        const assetNormalized = normalizeForMatching(assetName);
        const queryNormalized = normalizeForMatching(query);

        if (!assetNormalized || !queryNormalized) return false;

        // Exact match
        if (assetNormalized === queryNormalized) return true;

        // Contains match (either direction)
        if (
          assetNormalized.includes(queryNormalized) ||
          queryNormalized.includes(assetNormalized)
        ) {
          return true;
        }

        // Word-based matching: check if significant words match
        const assetWords = assetNormalized
          .split(/\s+/)
          .filter((w) => w.length > 2);
        const queryWords = queryNormalized
          .split(/\s+/)
          .filter((w) => w.length > 2);

        if (assetWords.length > 0 && queryWords.length > 0) {
          // Check if at least 50% of words match
          const matchingWords = queryWords.filter((qw) =>
            assetWords.some((aw) => aw.includes(qw) || qw.includes(aw))
          );
          if (matchingWords.length >= Math.ceil(queryWords.length * 0.5)) {
            return true;
          }
        }

        return false;
      };

      const matchedAsset = requiredAssets.find((asset) =>
        matchesAsset(asset.name, touchQuery)
      );

      if (matchedAsset) {
        const assetTempId = getAssetTempId(matchedAsset);

        // Set explicit asset references in moment
        vrMoment.assetId = assetTempId;
        vrMoment.assetName = matchedAsset.name;
        vrMoment.assetTempId = assetTempId; // Explicit tempId for Unity

        // Add moment to asset's interactions array
        const assetEntry = vrInteractions.assetMappings[assetTempId];
        if (assetEntry) {
          assetEntry.interactions.push({
            momentId: moment.id,
            interactionType: vrMoment.interaction.interactionType,
            required: vrMoment.interaction.required,
            sequence: assetEntry.interactions.length + 1, // Order within asset
          });

          // Track asset in chapter
          if (!vrChapter.assetIds.includes(assetTempId)) {
            vrChapter.assetIds.push(assetTempId);
          }
        }
      }

      vrChapter.moments.push(vrMoment);
    });

    vrInteractions.chapters.push(vrChapter);
  });

  // Build asset flow logic based on moment sequence
  vrInteractions.assets.forEach((asset, index) => {
    // Set next asset in sequence (if not last)
    if (index < vrInteractions.assets.length - 1) {
      asset.flow.nextAssetAfterCompletion =
        vrInteractions.assets[index + 1].tempId;
    }

    // Set prerequisite (previous asset in sequence)
    if (index > 0) {
      asset.flow.prerequisiteAssets = [vrInteractions.assets[index - 1].tempId];
    }
  });

  const { assets, assetMappings, ...output } = vrInteractions;
  return output;
}

/**
 * Validate training data before conversion (ASSET-CENTRIC validation)
 * @param {Array} chapters - Training chapters
 * @param {Array} requiredAssets - Required assets array
 * @returns {Object} Validation result
 */
export function validateTrainingData(chapters, requiredAssets = []) {
  const errors = [];
  const warnings = [];

  if (!chapters || chapters.length === 0) {
    errors.push('No chapters found. Please generate a storyboard first.');
    return { valid: false, errors, warnings };
  }

  // ASSET-CENTRIC VALIDATION: Ensure all required assets have moments
  const mandatoryAssets = requiredAssets.filter((a) => a.mandatory);
  const assetsInMoments = new Set();

  // Helper function for robust asset name matching
  const normalizeForMatching = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  };

  // Helper function to check if asset name matches query (fuzzy matching)
  const matchesAsset = (assetName, query) => {
    const assetNormalized = normalizeForMatching(assetName);
    const queryNormalized = normalizeForMatching(query);

    if (!assetNormalized || !queryNormalized) return false;

    // Exact match
    if (assetNormalized === queryNormalized) return true;

    // Contains match (either direction)
    if (
      assetNormalized.includes(queryNormalized) ||
      queryNormalized.includes(assetNormalized)
    ) {
      return true;
    }

    // Word-based matching: check if significant words match
    const assetWords = assetNormalized.split(/\s+/).filter((w) => w.length > 2);
    const queryWords = queryNormalized.split(/\s+/).filter((w) => w.length > 2);

    if (assetWords.length > 0 && queryWords.length > 0) {
      // Check if at least 50% of words match
      const matchingWords = queryWords.filter((qw) =>
        assetWords.some((aw) => aw.includes(qw) || qw.includes(aw))
      );
      if (matchingWords.length >= Math.ceil(queryWords.length * 0.5)) {
        return true;
      }
    }

    return false;
  };

  chapters.forEach((chapter, chapterIndex) => {
    if (!chapter.moments || chapter.moments.length === 0) {
      warnings.push(
        `Chapter ${chapterIndex + 1} (${chapter.title}) has no moments.`
      );
    }

    chapter.moments?.forEach((moment, momentIndex) => {
      const technical = moment.content?.technical || {};
      const nonTechnical = moment.content?.nonTechnical || {};

      // Check if moment has interaction query
      const touchQuery =
        technical.interaction?.touchObjectQuery ||
        nonTechnical.interaction?.touchObjectQuery;

      if (!touchQuery || touchQuery.trim() === '') {
        warnings.push(
          `Moment ${momentIndex + 1} in Chapter ${chapterIndex + 1} has no touch object query.`
        );
      } else {
        // Track which assets are referenced in moments using robust matching
        requiredAssets.forEach((asset) => {
          if (matchesAsset(asset.name, touchQuery)) {
            assetsInMoments.add(asset.id || asset.tempId);
          }
        });
      }

      // Check if moment has at least one voice over or display text
      const hasBeforeTouch =
        technical.beforeTouch?.voiceOver ||
        nonTechnical.beforeTouch?.voiceOver ||
        technical.beforeTouch?.displayText ||
        nonTechnical.beforeTouch?.displayText;

      if (!hasBeforeTouch) {
        warnings.push(
          `Moment ${momentIndex + 1} in Chapter ${chapterIndex + 1} has no before-touch content.`
        );
      }
    });
  });

  // Validate mandatory assets have moments
  mandatoryAssets.forEach((asset) => {
    const assetId = asset.id || asset.tempId;
    if (!assetsInMoments.has(assetId)) {
      // Check all touchObjectQuery values to see what was generated
      const allTouchQueries = [];
      chapters.forEach((chapter) => {
        chapter.moments?.forEach((moment) => {
          const technical = moment.content?.technical || {};
          const nonTechnical = moment.content?.nonTechnical || {};
          const touchQuery =
            technical.interaction?.touchObjectQuery ||
            nonTechnical.interaction?.touchObjectQuery;
          if (touchQuery) {
            allTouchQueries.push(touchQuery);
          }
        });
      });

      // Suggest possible matches
      const assetNameNormalized = normalizeForMatching(asset.name);
      const possibleMatches = allTouchQueries.filter((query) => {
        const queryNormalized = normalizeForMatching(query);
        return (
          queryNormalized.includes(assetNameNormalized) ||
          assetNameNormalized.includes(queryNormalized)
        );
      });

      if (possibleMatches.length > 0) {
        errors.push(
          `Mandatory asset "${asset.name}" has no moments. ` +
            `Found similar touchObjectQuery values: ${possibleMatches.join(', ')}. ` +
            `Please update the touchObjectQuery in the moment editor to match "${asset.name}" exactly or use a variation like "${asset.name.toLowerCase().replace(/\s+/g, '_')}".`
        );
      } else {
        errors.push(
          `Mandatory asset "${asset.name}" has no moments. ` +
            `Please generate a moment with touchObjectQuery matching "${asset.name}" or edit an existing moment's touchObjectQuery to reference this asset. ` +
            `Suggested touchObjectQuery: "${asset.name.toLowerCase().replace(/\s+/g, '_')}" or "${asset.name.toLowerCase().replace(/\s+/g, '')}".`
        );
      }
    }
  });

  // Validate all moments reference valid assets (using same robust matching)
  chapters.forEach((chapter, chapterIndex) => {
    chapter.moments?.forEach((moment, momentIndex) => {
      const technical = moment.content?.technical || {};
      const nonTechnical = moment.content?.nonTechnical || {};
      const touchQuery =
        technical.interaction?.touchObjectQuery ||
        nonTechnical.interaction?.touchObjectQuery ||
        '';

      if (touchQuery) {
        const matchesAnyAsset = requiredAssets.some((asset) =>
          matchesAsset(asset.name, touchQuery)
        );
        if (!matchesAnyAsset) {
          warnings.push(
            `Moment ${momentIndex + 1} in Chapter ${chapterIndex + 1} references "${touchQuery}" but no matching asset found in required assets.`
          );
        }
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
